/**
 * W10 — the KnowIt adapter for the "complement" wave (stars, pins, share links, search
 * filters, collection permissions, templates, exports, attachments).
 *
 * Handed a transport instead of reaching into `KnowItApi`, like every other wave's module.
 * Everything here is Outline-era RPC and answers `{ data, policies, pagination }`; the
 * translation of those envelopes into slate's vocabulary is this file's whole job.
 */
import { ApiError } from "~/api/errors";
import {
  ATTACHMENT_MAX_BYTES,
  type Attachment,
  AttachmentSchema,
  type CollectionGroupMembership,
  CollectionGroupMembershipSchema,
  type CollectionMembership,
  CollectionMembershipSchema,
  type CollectionPermission,
  type ExportFormat,
  type FileOperation,
  FileOperationSchema,
  type Pin,
  PinSchema,
  type RecentSearch,
  RecentSearchSchema,
  type SearchHit,
  SearchHitSchema,
  type SearchParams,
  type SearchResults,
  type ShareLink,
  type ShareLinkPatch,
  ShareLinkSchema,
  type Star,
  StarSchema,
  type StarTarget,
} from "~/api/libraryTypes";
import type { Document } from "~/api/types";

export interface LibraryTransport {
  /** Dot-RPC POST over the enveloped lane. Throws on a bodiless response. */
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /**
   * The SAME dot-RPC POST on the 204-tolerant lane, which answers `null` for a bodiless
   * success instead of failing to parse an empty body. `shares.info` needs it: "this document
   * has never been shared" is a **204**, not an error and not an empty list.
   */
  postTolerant(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** Multipart POST — the browser owns the boundary header; 204 resolves to null. */
  form(path: string, body: FormData): Promise<unknown>;
  /**
   * A bearer-carrying GET that resolves the BODY as a blob, following the redirect the platform
   * answers with. `fileOperations.redirect` 302s to a signed storage URL, and a plain
   * `<a href>` or `window.open` would arrive **anonymous** under a PKCE bearer session — the
   * same mistake that took the World page dark on 2026-08-18.
   */
  blob(path: string): Promise<Blob>;
  /** Maps a raw `PresentDocument` row + the envelope's policies into slate's `Document`. */
  toDocument(raw: unknown, envelope: unknown): Document;
}

interface Envelope {
  data?: unknown;
  policies?: Array<{ id: string; abilities: Record<string, boolean> }>;
  pagination?: { offset: number; limit: number; total: number };
}

function envelopeOf(value: unknown): Envelope {
  return (value ?? {}) as Envelope;
}

export function createKnowItLibrary(transport: LibraryTransport) {
  const stars = {
    /**
     * `{ pagination, data: { stars, documents }, policies }`. The documents ride along so the
     * sidebar can render titles from one call — mapped through the shared document mapper so a
     * starred row carries the same abilities as the same row anywhere else.
     */
    list: async (
      options: { limit?: number } = {},
    ): Promise<{ stars: Star[]; documents: Document[] }> => {
      const envelope = envelopeOf(
        await transport.post("stars.list", { limit: options.limit ?? 100 }),
      );
      const data = (envelope.data ?? {}) as { stars?: unknown[]; documents?: unknown[] };
      return {
        stars: (data.stars ?? []).map((row) => StarSchema.parse(row)),
        documents: (data.documents ?? []).map((row) => transport.toDocument(row, envelope)),
      };
    },

    /**
     * `stars.create` does NOT dedupe: a second call for the same target makes a second row and
     * the sidebar shows the document twice. The caller (`useToggleStar`) therefore resolves the
     * current list before creating — never this method, which must stay a single round trip so
     * a caller that already knows the answer does not pay for a second read.
     */
    create: async (target: StarTarget): Promise<Star> => {
      if (!target.documentId && !target.collectionId) {
        throw new ApiError("A star needs a document or a collection", 400);
      }
      const envelope = envelopeOf(
        await transport.post("stars.create", {
          ...(target.documentId ? { documentId: target.documentId } : {}),
          ...(target.collectionId ? { collectionId: target.collectionId } : {}),
        }),
      );
      return StarSchema.parse(envelope.data);
    },

    /** Manual ordering. The list itself is ordered by `updatedAt`, so this is a hint, not a sort. */
    reorder: async (input: { id: string; index: string }): Promise<Star> => {
      const envelope = envelopeOf(await transport.post("stars.update", input));
      return StarSchema.parse(envelope.data);
    },

    /** By the STAR's id, never the document's — `{ success: true }`, nothing to map. */
    delete: async (id: string): Promise<void> => {
      await transport.post("stars.delete", { id });
    },
  };

  const pins = {
    /**
     * ⚠ `pins.list` WITHOUT a `collectionId` returns only pins whose `collectionId IS NULL` —
     * the home pins — not "every pin". The controller's else-branch is an explicit
     * `Where(pin => pin.CollectionId == null)`, so "all pins" is not a question this endpoint
     * can be asked, and a caller that wanted one collection must pass it.
     */
    list: async (
      options: { collectionId?: string; limit?: number } = {},
    ): Promise<{ pins: Pin[]; documents: Document[] }> => {
      const envelope = envelopeOf(
        await transport.post("pins.list", {
          limit: options.limit ?? 100,
          ...(options.collectionId ? { collectionId: options.collectionId } : {}),
        }),
      );
      const data = (envelope.data ?? {}) as { pins?: unknown[]; documents?: unknown[] };
      return {
        pins: (data.pins ?? []).map((row) => PinSchema.parse(row)),
        documents: (data.documents ?? []).map((row) => transport.toDocument(row, envelope)),
      };
    },

    /** Like a star, no dedupe server-side — the caller resolves before it writes. */
    create: async (input: { documentId: string; collectionId?: string }): Promise<Pin> => {
      const envelope = envelopeOf(
        await transport.post("pins.create", {
          documentId: input.documentId,
          ...(input.collectionId ? { collectionId: input.collectionId } : {}),
        }),
      );
      return PinSchema.parse(envelope.data);
    },

    /** Pins ARE ordered by `index` (ascending) — unlike stars, this is the real sort key. */
    reorder: async (input: { id: string; index: string }): Promise<Pin> => {
      const envelope = envelopeOf(await transport.post("pins.update", input));
      return PinSchema.parse(envelope.data);
    },

    delete: async (id: string): Promise<void> => {
      await transport.post("pins.delete", { id });
    },
  };

  const shareLinks = {
    /**
     * ⚠ `shares.info` answers **204 No Content** for a document that has never been shared —
     * the ordinary state, not an error. The transport hands back `null` for a bodiless success,
     * and a caller that parsed the body unconditionally would render a dialog full of
     * `undefined`. Null here means "not shared yet", and only that.
     */
    forDocument: async (documentId: string): Promise<ShareLink | null> => {
      const raw = await transport.postTolerant("shares.info", { documentId });
      if (raw === null || raw === undefined) {
        return null;
      }
      const envelope = envelopeOf(raw);
      // `shares.info` nests as `{ data: { shares: [...] } }`; create/update return the row bare.
      const data = envelope.data as { shares?: unknown[] } | undefined;
      const first = Array.isArray(data?.shares) ? data.shares[0] : undefined;
      return first === undefined ? null : ShareLinkSchema.parse(first);
    },

    /**
     * An UPSERT, not a create: the controller looks for an existing non-revoked share on the
     * document and updates it instead of making a second link. So "share this" is idempotent
     * and a document never accumulates links.
     */
    create: async (input: { documentId: string } & ShareLinkPatch): Promise<ShareLink> => {
      const envelope = envelopeOf(
        await transport.post("shares.create", {
          documentId: input.documentId,
          ...(input.published === undefined ? {} : { published: input.published }),
          ...(input.includeChildDocuments === undefined
            ? {}
            : { includeChildDocuments: input.includeChildDocuments }),
          ...(input.allowIndexing === undefined ? {} : { allowIndexing: input.allowIndexing }),
        }),
      );
      return ShareLinkSchema.parse(envelope.data);
    },

    update: async (input: { id: string } & ShareLinkPatch): Promise<ShareLink> => {
      const envelope = envelopeOf(
        await transport.post("shares.update", {
          id: input.id,
          ...(input.published === undefined ? {} : { published: input.published }),
          ...(input.includeChildDocuments === undefined
            ? {}
            : { includeChildDocuments: input.includeChildDocuments }),
          ...(input.allowIndexing === undefined ? {} : { allowIndexing: input.allowIndexing }),
        }),
      );
      return ShareLinkSchema.parse(envelope.data);
    },

    /** `{ success: true }`; the soft delete is the server's business, not the caller's. */
    revoke: async (id: string): Promise<void> => {
      await transport.post("shares.revoke", { id });
    },
  };

  const exports = {
    /**
     * ⚠ Export is a JOB, not a download. The endpoint enqueues a Hangfire operation and answers
     * with a `FileOperation` row; there is no synchronous "give me the markdown" route on this
     * platform. The caller polls `status` and only then asks for the bytes.
     *
     * The artefact is always a `.zip` whatever the format — the storage key is built as
     * `{title}.zip` before the format is even parsed.
     */
    start: async (input: { documentId: string; format: ExportFormat }): Promise<FileOperation> => {
      const envelope = envelopeOf(
        await transport.post("documents.export", { id: input.documentId, format: input.format }),
      );
      return FileOperationSchema.parse(envelope.data);
    },

    status: async (operationId: string): Promise<FileOperation> => {
      const envelope = envelopeOf(await transport.post("fileOperations.info", { id: operationId }));
      return FileOperationSchema.parse(envelope.data);
    },

    /** The bytes, through the bearer-carrying lane — never a bare link. */
    download: (operationId: string): Promise<Blob> =>
      transport.blob(`fileOperations.redirect?id=${encodeURIComponent(operationId)}`),
  };

  const searches = {
    /**
     * The filtered search. Every filter is dropped when it is not set, because the request
     * record's nullable fields mean "no filter" only when ABSENT — and `statusFilter: []` is an
     * empty array the server reads as "no filter" too, which is a coincidence worth not relying
     * on.
     *
     * ⚠ A blank query short-circuits the endpoint to `{pagination:{0,0,0}, data:[], policies:[]}`
     * BEFORE the filters run, so the caller must not present filtering as usable on its own.
     */
    run: async (params: SearchParams): Promise<SearchResults> => {
      const envelope = envelopeOf(
        await transport.post("documents.search", {
          query: params.query,
          limit: params.limit ?? 25,
          ...(params.offset ? { offset: params.offset } : {}),
          ...(params.collectionId ? { collectionId: params.collectionId } : {}),
          ...(params.userId ? { userId: params.userId } : {}),
          ...(params.dateFilter ? { dateFilter: params.dateFilter } : {}),
          ...(params.statusFilter?.length ? { statusFilter: params.statusFilter } : {}),
        }),
      );
      const rows = Array.isArray(envelope.data) ? envelope.data : [];
      const hits: SearchHit[] = rows.map((row) => {
        const entry = (row ?? {}) as { id?: string; ranking?: number; context?: string };
        const document = transport.toDocument(
          (row as { document?: unknown }).document ?? row,
          envelope,
        );
        return SearchHitSchema.parse({
          id: entry.id ?? document.id,
          ranking: entry.ranking ?? 0,
          context: entry.context ?? "",
          document,
        });
      });
      return { total: envelope.pagination?.total ?? hits.length, hits };
    },

    /** `searches.list` carries NO `policies` block — these rows are the viewer's own. */
    recent: async (options: { limit?: number } = {}): Promise<RecentSearch[]> => {
      const envelope = envelopeOf(
        await transport.post("searches.list", { limit: options.limit ?? 10 }),
      );
      const rows = Array.isArray(envelope.data) ? envelope.data : [];
      return rows.map((row) => RecentSearchSchema.parse(row));
    },

    /** By id, or by the query TEXT — which forgets every repetition of the same search. */
    forget: async (input: { id?: string; query?: string }): Promise<void> => {
      await transport.post("searches.delete", {
        ...(input.id ? { id: input.id } : {}),
        ...(input.query ? { query: input.query } : {}),
      });
    },
  };

  const collectionPeople = {
    /**
     * `{ pagination, data: { memberships, users } }`. The `query` field on the request record is
     * accepted and NEVER used by the handler (no `.Where` on it), so filtering by name is the
     * client's job — sending it would look like a server-side search that is not there.
     */
    list: async (
      collectionId: string,
    ): Promise<{ memberships: CollectionMembership[]; users: unknown[] }> => {
      const envelope = envelopeOf(
        await transport.post("collections.memberships", { id: collectionId, limit: 100 }),
      );
      const data = (envelope.data ?? {}) as { memberships?: unknown[]; users?: unknown[] };
      return {
        memberships: (data.memberships ?? []).map((row) => CollectionMembershipSchema.parse(row)),
        users: data.users ?? [],
      };
    },

    /** Always sends the UNDERSCORED spelling — the one the parser matches exactly. */
    add: async (input: {
      collectionId: string;
      userId: string;
      permission: CollectionPermission;
    }): Promise<CollectionMembership> => {
      const envelope = envelopeOf(
        await transport.post("collections.add_user", {
          id: input.collectionId,
          userId: input.userId,
          permission: input.permission,
        }),
      );
      const data = (envelope.data ?? {}) as { memberships?: unknown[] };
      const first = (data.memberships ?? [])[0];
      return CollectionMembershipSchema.parse(first);
    },

    remove: async (input: { collectionId: string; userId: string }): Promise<void> => {
      await transport.post("collections.remove_user", {
        id: input.collectionId,
        userId: input.userId,
      });
    },

    groups: async (
      collectionId: string,
    ): Promise<{ memberships: CollectionGroupMembership[]; groups: unknown[] }> => {
      const envelope = envelopeOf(
        await transport.post("collections.group_memberships", { id: collectionId, limit: 100 }),
      );
      const data = (envelope.data ?? {}) as { groupMemberships?: unknown[]; groups?: unknown[] };
      return {
        memberships: (data.groupMemberships ?? []).map((row) =>
          CollectionGroupMembershipSchema.parse(row),
        ),
        groups: data.groups ?? [],
      };
    },

    /**
     * ⚠ `collections.add_group` answers a bare `{ success: true }` — NOT the membership it just
     * made. So there is nothing to return and the caller must refetch; a client that patched a
     * row it never received would be inventing one.
     */
    addGroup: async (input: {
      collectionId: string;
      groupId: string;
      permission: CollectionPermission;
    }): Promise<void> => {
      await transport.post("collections.add_group", {
        id: input.collectionId,
        groupId: input.groupId,
        permission: input.permission,
      });
    },

    removeGroup: async (input: { collectionId: string; groupId: string }): Promise<void> => {
      await transport.post("collections.remove_group", {
        id: input.collectionId,
        groupId: input.groupId,
      });
    },
  };

  const templates = {
    /**
     * `documents.list` EXCLUDES templates unless asked (`Template = true`), which is why a
     * plain `useDocuments` listing "templates" was listing ordinary documents.
     */
    list: async (): Promise<Document[]> => {
      const envelope = envelopeOf(
        await transport.post("documents.list", { template: true, limit: 100 }),
      );
      const rows = Array.isArray(envelope.data) ? envelope.data : [];
      return rows.map((row) => transport.toDocument(row, envelope));
    },

    /**
     * Create a template FROM a document, without destroying it.
     *
     * ⚠ `documents.templatize` MUTATES the source (`document.MarkAsTemplate()`) — it converts,
     * it does not copy. So "create a template from this" is a `documents.create` with the
     * source's body and `template: true`, which leaves the original a document. `promote` below
     * is the destructive verb, offered separately and named for what it does.
     */
    createFrom: async (input: {
      documentId: string;
      title: string;
      text: string;
      collectionId?: string | null;
    }): Promise<Document> => {
      const envelope = envelopeOf(
        await transport.post("documents.create", {
          title: input.title,
          text: input.text,
          template: true,
          ...(input.collectionId ? { collectionId: input.collectionId } : {}),
        }),
      );
      return transport.toDocument(envelope.data, envelope);
    },

    /** ⚠ Destructive: the document BECOMES a template and there is no way back (no un-templatize). */
    promote: async (input: { documentId: string; collectionId?: string }): Promise<Document> => {
      const envelope = envelopeOf(
        await transport.post("documents.templatize", {
          id: input.documentId,
          ...(input.collectionId ? { collectionId: input.collectionId } : {}),
        }),
      );
      return transport.toDocument(envelope.data, envelope);
    },

    /**
     * A new document seeded from a template. ONE call: the server copies the template's
     * content, text, icon and colour itself, and refuses with `template_not_found` unless the
     * id resolves to a team-scoped, actually-marked-as-template, readable document.
     */
    createDocument: async (input: {
      templateId: string;
      title?: string;
      collectionId?: string | null;
    }): Promise<Document> => {
      const envelope = envelopeOf(
        await transport.post("documents.create", {
          templateId: input.templateId,
          ...(input.title ? { title: input.title } : {}),
          ...(input.collectionId ? { collectionId: input.collectionId, publish: true } : {}),
        }),
      );
      return transport.toDocument(envelope.data, envelope);
    },
  };

  const attachments = {
    /**
     * Reserve, then upload. The embeddable URL is the one from step ONE
     * (`/api/attachments.redirect?id=…`): `attachments.upload` answers 204 with no body, so
     * there is nothing to parse there and nothing to wait for beyond the status.
     *
     * `documentId` is required by the server (`DocumentId or CommentId is required`), which is
     * why an image cannot be added before its document exists.
     */
    upload: async (input: { file: File; documentId: string }): Promise<Attachment> => {
      if (input.file.size > ATTACHMENT_MAX_BYTES) {
        throw new ApiError("That file is larger than the 50 MB limit", 413);
      }
      const created = envelopeOf(
        await transport.post("attachments.create", {
          name: input.file.name,
          size: input.file.size,
          contentType: input.file.type || "application/octet-stream",
          documentId: input.documentId,
        }),
      );
      const data = (created.data ?? {}) as {
        uploadUrl?: string;
        form?: { attachmentId?: string };
        attachment?: unknown;
      };
      const attachment = AttachmentSchema.parse(data.attachment);

      const body = new FormData();
      body.append("attachmentId", data.form?.attachmentId ?? attachment.id);
      body.append("file", input.file, input.file.name);
      // The server names the route in `uploadUrl`; honour it rather than hard-coding a path
      // that a future deployment could move.
      await transport.form(
        (data.uploadUrl ?? "/api/attachments.upload").replace(/^\/api\//, ""),
        body,
      );

      return attachment;
    },

    delete: async (id: string): Promise<void> => {
      await transport.post("attachments.delete", { id });
    },
  };

  return {
    stars,
    pins,
    shareLinks,
    exports,
    searches,
    collectionPeople,
    templates,
    attachments,
  };
}
